"use client";

import Link from "next/link";
import CdnImage from "@/components/CdnImage";
import { playerSlug, hasPlayerProfilePage } from "@/lib/sports-player-slug";

interface Player {
  name: string;
  logo?: string; // country flag
  score?: number; // games in current set (live)
  record?: string; // e.g. "12-5" season record
}

interface TennisHeroProps {
  home: Player;
  away: Player;
  /** Comma-separated set scores like "6-3, 2-0". */
  rawScore?: string;
  /** Current set identifier like "S2" (live matches). */
  period?: string;
  live?: boolean;
  ended?: boolean;
  winnerHome?: boolean;
  winnerAway?: boolean;
  /** League slug ("atp" / "wta") — used to build /sports/players/[league]/[slug] links from each player name. */
  league?: string;
}

/**
 * Polymarket-style tennis scoreboard. Two rows stacked vertically — flag +
 * name on the left, set-by-set scores in individual cells to the right.
 * Current set and status live above the rows.
 *
 * Parses `rawScore` ("6-3, 2-0") into per-player per-set numbers. Falls back
 * to an empty grid if the score string isn't present (pre-match).
 */
function abbreviatePlayerName(full: string): string {
  // "Yasmine Kabbaj" → "Y. Kabbaj". Hyphenated first names ("Jean-Luc Lopez"
  // → "J-L. Lopez") and multi-part surnames ("Carlos Alcaraz Garfia" →
  // "C. Alcaraz Garfia") are preserved.
  if (!full) return full;
  const parts = full.trim().split(/\s+/);
  if (parts.length < 2) return full;
  const first = parts[0];
  const surname = parts.slice(1).join(" ");
  const initials = first.split("-").map((p) => p.charAt(0).toUpperCase()).join("-");
  return `${initials}. ${surname}`;
}
export default function TennisHero({
  home,
  away,
  rawScore,
  period,
  live,
  ended,
  winnerHome,
  winnerAway,
  league,
}: TennisHeroProps) {
  const playerSlugFor = (name: string) => playerSlug(name);
  // Parse "6-3, 2-0" → [[6,3], [2,0]]
  const setPairs: Array<[number | null, number | null]> = (rawScore || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => {
      const [a, b] = s.split("-").map((x) => {
        const n = parseInt(x, 10);
        return Number.isNaN(n) ? null : n;
      });
      return [a ?? null, b ?? null];
    });

  // Always render at least 3 cells (best-of-3) so the layout doesn't jump
  // when a set finishes.
  const minSets = 3;
  const totalSets = Math.max(minSets, setPairs.length);

  const homeSets = Array.from({ length: totalSets }, (_, i) => setPairs[i]?.[0] ?? null);
  const awaySets = Array.from({ length: totalSets }, (_, i) => setPairs[i]?.[1] ?? null);

  // Indices of completed sets (both players have a value, not active set).
  const currentSetIdx = setPairs.length > 0 ? setPairs.length - 1 : -1;

  // Per-set winner:
  //   'home' if homeGames > awayGames AND the set is not the active live one
  //   'away' if awayGames > homeGames AND the set is not the active live one
  //   null when tied, live, or missing data
  const setWinners: Array<'home' | 'away' | null> = Array.from({ length: totalSets }, (_, i) => {
    const h = homeSets[i];
    const a = awaySets[i];
    if (h == null || a == null) return null;
    // If this is the live in-progress set we don't declare a winner yet.
    if (live && i === currentSetIdx) return null;
    if (h > a) return 'home';
    if (a > h) return 'away';
    return null;
  });

  const renderRow = (
    p: Player,
    sets: Array<number | null>,
    side: 'home' | 'away',
    matchWinner: boolean,
  ) => {
    // Once the match has ended, dim the loser noticeably so the winner row
    // is visually dominant.
    const matchIsOver = ended;
    const loserRow = matchIsOver && !matchWinner;
    return (
    <div className={`flex items-center gap-3 py-2.5 transition-opacity ${loserRow ? 'opacity-50' : ''}`}>
      {/* Flag + name */}
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        {p.logo ? (
          <CdnImage
            src={p.logo}
            alt=""
            width={28}
            height={20}
            className="w-7 h-5 rounded-sm object-cover flex-shrink-0"
          />
        ) : (
          <span className="w-7 h-5 rounded-sm bg-gray-200 dark:bg-[#1e1e24] flex-shrink-0" />
        )}
        <div className="flex items-center gap-2 min-w-0">
          {league && p.name && hasPlayerProfilePage(league) ? (
            <Link
              href={`/sports/players/${league}/${playerSlugFor(p.name)}`}
              onClick={(e) => e.stopPropagation()}
              className={`font-inter-tight truncate text-gray-900 dark:text-white hover:underline underline-offset-2 ${
                matchWinner ? "text-lg md:text-xl font-semibold" : "text-base md:text-lg"
              }`}
            >
              {abbreviatePlayerName(p.name)}
            </Link>
          ) : (
            <span
              className={`font-inter-tight truncate text-gray-900 dark:text-white ${
                matchWinner ? "text-lg md:text-xl font-semibold" : "text-base md:text-lg"
              }`}
            >
              {abbreviatePlayerName(p.name)}
            </span>
          )}
          {p.record && p.record !== '0-0' && (
            <span className="text-[11px] font-inter-tight text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0">
              {p.record}
            </span>
          )}
        </div>
      </div>
      {/* Set cells — each cell shows this player's games in that set. The
          winner of the set gets full-weight text; the loser is dimmed.
          The current live set highlights its own background. */}
      <div className="flex items-center gap-2 md:gap-3 tabular-nums flex-shrink-0">
        {sets.map((v, i) => {
          const isCurrent = live && i === currentSetIdx;
          const filled = v != null;
          const wonSet = setWinners[i] === side;
          const lostSet = setWinners[i] != null && setWinners[i] !== side;
          return (
            <span
              key={i}
              className={`inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-[5px] text-xl md:text-2xl transition-colors ${
                isCurrent
                  ? "bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white font-semibold"
                  : wonSet
                  ? "text-gray-900 dark:text-white font-bold"
                  : lostSet
                  ? "text-gray-400 dark:text-gray-500 font-normal"
                  : filled
                  ? "text-gray-700 dark:text-gray-300 font-semibold"
                  : "text-gray-300 dark:text-gray-700 font-semibold"
              }`}
            >
              {filled ? v : "–"}
            </span>
          );
        })}
      </div>
    </div>
    );
  };

  return (
    <div className="px-4 md:px-8 py-4">
      {/* Rows only — status line moved up next to the tournament badge so
          "Live · S2" reads before "Madrid Open, Qualification". */}
      <div className="max-w-xl mx-auto divide-y divide-gray-100 dark:divide-[#1e1e24]">
        {renderRow(home, homeSets, 'home', !!winnerHome)}
        {renderRow(away, awaySets, 'away', !!winnerAway)}
      </div>
    </div>
  );
}
